import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { CAMPAIGN_SUMMARY_METRICS } from "./dashboardHooks";
import { AMAZON_OVERVIEW_METRICS } from "./OverviewCardSection/amazonOverviewConfig";

function FilterSection({ title, children }) {
  return (
    <Box
      sx={{
        py: 2,
        borderBottom: "1px solid #E0E0E0",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Typography sx={{ fontSize: "14px", fontWeight: 600, mb: 1.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function MetricSelect({ label, value, onChange, options }) {
  return (
    <FormControl size="small" sx={{ minWidth: 160, flex: 1 }}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <MenuItem key={option.key} value={option.key}>
            {option.label || option.shortTitle}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default function DashboardFiltersPanel({
  filters,
  onChange,
  showProductSummary = false,
}) {
  const set = (key) => (value) => onChange({ [key]: value });

  return (
    <Box>
      <FilterSection title="Overview">
        <Typography variant="body2" color="text.secondary">
          Portfolios are chosen from Choose Portfolios on the dashboard. Overview
          cards are chosen from Manage Widgets.
        </Typography>
      </FilterSection>

      <FilterSection title="Performance Analysis">
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 1.5 }}>
          <MetricSelect
            label="Left axis"
            value={filters.chartLeftMetric}
            onChange={set("chartLeftMetric")}
            options={AMAZON_OVERVIEW_METRICS}
          />
          <MetricSelect
            label="Right axis"
            value={filters.chartRightMetric}
            onChange={set("chartRightMetric")}
            options={AMAZON_OVERVIEW_METRICS}
          />
        </Box>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={filters.chartTimePeriod}
          onChange={(_, value) => value && set("chartTimePeriod")(value)}
        >
          <ToggleButton value="date" sx={{ textTransform: "none" }}>Day</ToggleButton>
          <ToggleButton value="week" sx={{ textTransform: "none" }}>Week</ToggleButton>
          <ToggleButton value="month" sx={{ textTransform: "none" }}>Month</ToggleButton>
        </ToggleButtonGroup>
      </FilterSection>

      <FilterSection title="Campaign Summary">
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
          <MetricSelect
            label="Base metric"
            value={filters.campaignMetric}
            onChange={set("campaignMetric")}
            options={CAMPAIGN_SUMMARY_METRICS}
          />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={filters.campaignTab}
            onChange={(_, value) => value && set("campaignTab")(value)}
          >
            <ToggleButton value="vs" sx={{ textTransform: "none" }}>Prospect vs Retargeting</ToggleButton>
            <ToggleButton value="prospect" sx={{ textTransform: "none" }}>Prospect</ToggleButton>
            <ToggleButton value="retarget" sx={{ textTransform: "none" }}>Retargeting</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </FilterSection>

      <FilterSection title="Category & Product Performance">
        <ToggleButtonGroup
          exclusive
          size="small"
          value={filters.performanceView}
          onChange={(_, value) => value && set("performanceView")(value)}
        >
          <ToggleButton value="both" sx={{ textTransform: "none" }}>Both</ToggleButton>
          <ToggleButton value="category" sx={{ textTransform: "none" }}>Category</ToggleButton>
          <ToggleButton value="product" sx={{ textTransform: "none" }}>Product</ToggleButton>
        </ToggleButtonGroup>
      </FilterSection>

      <FilterSection title="Targeting Insights">
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={filters.targetingView}
            onChange={(_, value) => value && set("targetingView")(value)}
          >
            <ToggleButton value="Default" sx={{ textTransform: "none" }}>Default</ToggleButton>
            <ToggleButton value="Funnel" sx={{ textTransform: "none" }}>Funnel</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={filters.targetingPerformer}
            onChange={(_, value) => value && set("targetingPerformer")(value)}
          >
            <ToggleButton value="top" sx={{ textTransform: "none" }}>Top 10</ToggleButton>
            <ToggleButton value="bottom" sx={{ textTransform: "none" }}>Bottom 10</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </FilterSection>

      {showProductSummary ? (
        <FilterSection title="Product Summary">
          <ToggleButtonGroup
            exclusive
            size="small"
            value={filters.productSummaryView}
            onChange={(_, value) => value && set("productSummaryView")(value)}
          >
            <ToggleButton value="product-summary-low-stock-product" sx={{ textTransform: "none" }}>
              Low Stock
            </ToggleButton>
            <ToggleButton value="product-summary-zero-spend-product" sx={{ textTransform: "none" }}>
              Zero Ad Spend
            </ToggleButton>
          </ToggleButtonGroup>
        </FilterSection>
      ) : null}
    </Box>
  );
}
